import type { CSSProperties } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { IInfluencer } from '../../../redux/buzzbridge/types';
import { formatSocialHandle } from '../../../utils/socialHandle';

/** Portrait headshots — anchor crop from the top so faces stay visible in banners and avatars. */
export const influencerProfilePhotoCoverSx = {
    objectFit: 'cover' as const,
    objectPosition: 'top' as const,
};

export const influencerProfileAvatarSx: SxProps<Theme> = {
    '& .MuiAvatar-img': influencerProfilePhotoCoverSx,
};

export const influencerProfilePhotoImgStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top',
};

export function sumPlatformFollowers(influencer: IInfluencer): number {
    let n = 0;
    if (influencer.instagram?.followers) n += influencer.instagram.followers;
    if (influencer.youtube?.followers) n += influencer.youtube.followers;
    if (influencer.tiktok?.followers) n += influencer.tiktok.followers;
    return n;
}

function averagePlatformEngagementPercent(influencer: IInfluencer): { value: number; label: string } {
    let total = 0;
    let count = 0;
    const add = (er?: number) => {
        if (er == null || Number.isNaN(er)) return;
        total += er <= 1 ? er * 100 : er;
        count += 1;
    };
    add(influencer.instagram?.engagementRate);
    add(influencer.youtube?.engagementRate);
    add(influencer.tiktok?.engagementRate);
    if (count === 0) return { value: 0, label: '0%' };
    const v = total / count;
    return { value: Math.min(100, v), label: `${v.toFixed(1)}%` };
}

export function engagementFromInfluencer(influencer: IInfluencer): { value: number; label: string } {
    const ce = influencer.calculatedEngagement;
    if (ce != null && !Number.isNaN(Number(ce))) {
        const n = Number(ce);
        const pct = n <= 1 ? n * 100 : n;
        return { value: Math.min(100, pct), label: `${pct.toFixed(1)}%` };
    }
    return averagePlatformEngagementPercent(influencer);
}

export function totalReachDisplay(influencer: IInfluencer): string {
    const cr = influencer.calculatedReach;
    if (typeof cr === 'number' && cr > 0) return Math.round(cr).toLocaleString();
    const sum = sumPlatformFollowers(influencer);
    return sum > 0 ? sum.toLocaleString() : '0';
}

export function primaryHandle(influencer: IInfluencer): string {
    const createdBy = influencer?.created_by;
    const u =
        (typeof createdBy === 'object' && createdBy !== null ? createdBy.userName : undefined) ||
        influencer.instagram?.username ||
        influencer.tiktok?.username ||
        influencer.youtube?.username ||
        influencer.thspian?.username;
    return u ? formatSocialHandle(u, '@creator') : '@creator';
}

export function isCreatorVerified(influencer: IInfluencer): boolean {
    return Boolean(
        influencer.verified_by_platform ||
            influencer.instagram?.verified ||
            influencer.tiktok?.verified ||
            influencer.youtube?.verified,
    );
}

export function formatFollowersShort(n?: number): string {
    if (n == null || n <= 0) return 'Connected';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M Followers`;
    if (n >= 1000) return `${Math.round(n / 1000)}k Followers`;
    return `${n} Followers`;
}
